import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Chip,
  Skeleton,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaidIcon from '@mui/icons-material/Paid';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

const StatItem = ({ icon, value, label, color = 'text.secondary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
      {icon}
    </Box>
    <Typography variant="caption" color="text.secondary">
      {value} {label}
    </Typography>
  </Box>
);

const ProjectCard = ({
  project,
  stats,
  onClick,
  loading = false,
  variant = 'default', // 'default' | 'compact'
  showStats = true,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="80%" />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={60} height={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const hasStats = stats && showStats;

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={onClick} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FolderIcon sx={{ fontSize: variant === 'compact' ? 24 : 32 }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant={variant === 'compact' ? 'subtitle1' : 'h6'}
                noWrap
                sx={{ fontWeight: 600 }}
              >
                {project.name}
              </Typography>
              {project.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {project.description}
                </Typography>
              )}
            </Box>
          </Box>

          {hasStats && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <StatItem
                  icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                  value={stats.total || 0}
                  label={t('projectCard.documents')}
                />
                {stats.viewed > 0 && (
                  <StatItem
                    icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                    value={stats.viewed}
                    label={t('projectCard.viewed')}
                    color="warning.main"
                  />
                )}
                {stats.paid > 0 && (
                  <StatItem
                    icon={<PaidIcon sx={{ fontSize: 16 }} />}
                    value={stats.paid}
                    label={t('projectCard.paid')}
                    color="success.main"
                  />
                )}
                {stats.downloaded > 0 && (
                  <StatItem
                    icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    value={stats.downloaded}
                    label={t('projectCard.downloaded')}
                    color="primary.main"
                  />
                )}
              </Box>
            </Box>
          )}

          {hasStats && stats.viewed > 0 && stats.paid === 0 && (
            <Box sx={{ mt: 2 }}>
              <Chip
                size="small"
                label={t('projectCard.pendingPayment')}
                color="warning"
                variant="outlined"
              />
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProjectCard;
